import {
    useEffect,
    useMemo
} from 'react';
import { useSelector } from 'react-redux';

import {
    React,
    View
} from '../../../../common';
import { ConsentCheckbox } from '../../../../common/ConsentCheckbox';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    ONLINE_BOOKING_CHILD_CLINIC,
    ONLINE_BOOKING_CLINIC,
    STORE_PROFILE_CHILD,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../../../../hooks/usePersonalData';
import { getConsents } from '../../../../selectors/consents';
import {
    IS_FOR_CHILD,
    IS_OTHER
} from './consts';

export const ImportVisitAgreements = ({save, selectedPatient, onChange, providerCode, isLoading}) => {
    const allConsents = useSelector(getConsents);
    const isSelected = useMemo(() => !!selectedPatient?.selected, [selectedPatient?.selected])
    const isOther = useMemo(() => selectedPatient?.id === IS_OTHER, [selectedPatient?.id]);
    const isChild = useMemo(() => selectedPatient?.id === IS_FOR_CHILD, [selectedPatient?.id]);
    const [currentChildConsent, setCurrentChildConsent] = React.useState(false);
    const [currentHistoryConsent, setCurrentHistoryConsent] = React.useState(false);
    const [currentClinicHistoryConsent, setCurrentClinicHistoryConsent] = React.useState(false);

    const storeKey = STORE_PROFILE_CHILD;
    const historyKey = useMemo(() => isChild ? CHILD_HISTORY_KEY : USER_HISTORY_KEY, [isChild]);
    const clinicHistoryKey = useMemo(() => isChild ? CHILD_CLINIC_HISTORY_KEY : USER_CLINIC_HISTORY_KEY, [isChild]);
    const clinicConsentKey = useMemo(() => isChild ? ONLINE_BOOKING_CHILD_CLINIC : ONLINE_BOOKING_CLINIC, [isChild]);

    const historyConsentVisibleFromStore = useMemo(() => {
        if (isChild) {
            return true;
        }
        return !allConsents?.user?.main?.[USER_HISTORY_KEY];
    }, [allConsents?.user?.main, isChild]);

    const clinicHistoryConsentVisibleFromStore = useMemo(() => {
        if (isChild) {
            return true;
        }
        return !allConsents?.user?.[providerCode]?.[USER_CLINIC_HISTORY_KEY];
    }, [allConsents?.user,
        isChild,
        providerCode]);

    const consentVisibilityWhileLoadingRef = React.useRef(null);
    const wasLoadingRef = React.useRef(false);
    if (isLoading && !wasLoadingRef.current) {
        consentVisibilityWhileLoadingRef.current = {
            history: historyConsentVisibleFromStore,
            clinic: clinicHistoryConsentVisibleFromStore,
        };
    }
    if (!isLoading) {
        consentVisibilityWhileLoadingRef.current = null;
    }
    wasLoadingRef.current = isLoading;

    const isHistoryConsentVisible = isLoading && consentVisibilityWhileLoadingRef.current
        ? consentVisibilityWhileLoadingRef.current.history
        : historyConsentVisibleFromStore;

    const isClinicHistoryConsentVisible = isLoading && consentVisibilityWhileLoadingRef.current
        ? consentVisibilityWhileLoadingRef.current.clinic
        : clinicHistoryConsentVisibleFromStore;

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (!isSelected) {
            onChange?.({consent: false});
        }
    }, [isLoading,
        isSelected,
        onChange]);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (!save) {
            onChange?.({consent: true});
        } else {
            const consents = {};
            if (isChild) {
                consents[historyKey] = currentHistoryConsent;
                consents[clinicHistoryKey] = currentClinicHistoryConsent;
                consents[storeKey] = currentChildConsent;
            } else {
                if (isHistoryConsentVisible) {
                    consents[historyKey] = currentHistoryConsent;
                }
                if (isClinicHistoryConsentVisible) {
                    consents[clinicHistoryKey] = currentClinicHistoryConsent;
                }
            }
            onChange?.({
                consent: isChild ? currentChildConsent : true,
                consents,
                isHistoryConsentVisible, isClinicHistoryConsentVisible,
            });
        }
    }, [clinicConsentKey,
        clinicHistoryKey,
        currentChildConsent,
        currentClinicHistoryConsent,
        currentHistoryConsent,
        historyKey,
        isChild,
        isClinicHistoryConsentVisible,
        isHistoryConsentVisible,
        onChange,
        save,
        storeKey,
        isLoading]);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (isOther) {
            onChange?.({consent: true});
        }
    }, [isLoading,
        isOther,
        onChange]);

    if (!isSelected || !save || isOther) {
        return null;
    }

    return (<>
        <View style={[{ paddingHorizontal: 12, marginBottom: 12 }]}>
            {isClinicHistoryConsentVisible && <ConsentCheckbox
                providerCode={providerCode}
                onChange={setCurrentClinicHistoryConsent}
                agreementKey={clinicHistoryKey}
                containerStyle={{ marginTop: 12 }}
            />}
            {isChild && <ConsentCheckbox
                providerCode={providerCode}
                agreementKey={storeKey}
                onChange={setCurrentChildConsent}
                containerStyle={{ marginTop: 12 }} />}
            {isHistoryConsentVisible && <ConsentCheckbox
                providerCode={providerCode}
                onChange={setCurrentHistoryConsent}
                agreementKey={historyKey}
                containerStyle={{ marginTop: 12 }}
            />}
        </View>
    </>);
}
